import modalPricingUrl from '../modal-pricing.html';
import modalGuardUrl from '../modal-guard.html';
import modalCongratulationsUrl from '../modal-congratulations.html';
import modalTrialOfferUrl from '../modal-trial-offer.html';
import modalToastTrialOfferUrl from '../modal-toast-trial-offer.html';
import modalTrialReactivationFirstUrl from '../modal-trial-reactivation-first.html';
import modalTrialReactivationCycleUrl from '../modal-trial-reactivation-cycle.html';
import modalPaymentFailedUrl from '../modal-payment-failed.html';
import modalCardExpiresSoonUrl from '../modal-card-expires-soon.html';
import modalAutoAssigninglUrl from '../modal-auto-assigning.html';
import {
  SLING_FREE,
  SLING_PREMIUM,
  SLING_OLD_PREMIUM,
  FEATURE_ACCESS,
  PREMIUM_FEATURES_INFO,
  SUBSCRIPTION_LEVELS,
  SUBSCRIPTION_TRIAL_CYCLICAL_REACTIVATION_DAYS,
  SUBSCRIPTION_TRIAL_FIRST_REACTIVATION_DAYS,
  AUTO_ASSIGNING_MODAL_MODES,
} from '../constants';

class AccountService {
  constructor(
    Authentication,
    Utils,
    Restangular,
    Invoices,
    IntegrationService,
    GoogleAnalytics,
    PersonaCollection,
    SlingAnalytics,
    SubscriptionUtils,
    sdk,
    toSubscriptionNameFilter,
    $state,
    $uibModal,
    $rootScope,
    $timeout
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      Utils,
      Restangular,
      Invoices,
      IntegrationService,
      GoogleAnalytics,
      PersonaCollection,
      SlingAnalytics,
      SubscriptionUtils,
      sdk,
      toSubscriptionNameFilter,
      $state,
      $uibModal,
      $rootScope,
      $timeout,
    });
    this.SUBSCRIPTION_NAMES = sdk('constants').SUBSCRIPTION_NAMES;
  }

  // eslint-disable-next-line complexity
  tryShowPremiumReactivationPopup() {
    if (this.Authentication.user.org.isToastBilling) {
      return;
    }

    const { org } = this.Authentication.user;
    if (
      !this.Authentication.user.isManager ||
      org.subscriptionStartDate ||
      org.subscriptionLevel ||
      !org.premiumTrialStartDate
    ) {
      return;
    }

    const now = this.Utils.getNowDate();
    const { trialReactivationDismissed } = this.Authentication.user.settings.clientFlags;

    let from = org.premiumTrialStartDate;
    if (trialReactivationDismissed && new Date(trialReactivationDismissed) > from) {
      from = trialReactivationDismissed;
    }

    if (org.subscriptionCancelDate && org.subscriptionCancelDate > from) {
      from = org.subscriptionCancelDate;
    }

    const firstReactivationDate = this.Utils.addDays(
      from,
      SUBSCRIPTION_TRIAL_FIRST_REACTIVATION_DAYS
    );
    const cyclicalReactivationDate = this.Utils.addDays(
      from,
      SUBSCRIPTION_TRIAL_CYCLICAL_REACTIVATION_DAYS
    );

    if (
      org.isEligibleForFirstTrialReactivation &&
      !org.trialReactivated &&
      !org.subscriptionCancelDate &&
      !trialReactivationDismissed &&
      now >= firstReactivationDate
    ) {
      return this.openTrialReactivationModal(true);
    }

    if (now >= cyclicalReactivationDate) {
      return this.openTrialReactivationModal();
    }
  }

  _updateOrgBasedOnSubscription(subscription) {
    const data = this.Utils.extendWithProps(subscription, {}, [
      'isPremium',
      'premiumTrialStartDate',
      'subscriptionStartDate',
      'subscriptionLevel',
      'isBusinessSubscription',
      'nextChargeDate',
      'trialDays',
    ]);
    data.subscriptionPlanName = subscription.planName;

    this.Authentication.updateCurrentOrg(data);
  }

  startTrial = async (skipCongratulations) => {
    const subscription = await this.Restangular.all('account/org/subscription/trial').post();
    this.Restangular.restangularizeElement(null, subscription, 'account/org/subscription');
    this._updateOrgBasedOnSubscription(subscription);

    if (!skipCongratulations) {
      await this.trialStartCongratulations();
    }

    if (subscription.isPremium) {
      this.Authentication.user.hasAccessToLaborCost =
        this.Authentication.user.wouldHaveAccessToLaborCost;
    }

    if (this.$state.current.name === 'account.overview.purchase') {
      this.$state.go('account.overview.billing', {}, { reload: true });
    } else if (this.$state.current.name.endsWith('.preview')) {
      this.$state.go(this.$state.current.name.replace('.preview', ''), {}, { reload: true });
    }
  };

  trialStartCongratulations() {
    const { subscriptionPlanName, trialDays } = this.Authentication.user.org;
    const description = `You have activated your ${subscriptionPlanName} trial.\nAll paid features will be accessible for the next ${trialDays} days.`;
    return this.openCongratulationsModal(description, 'Get started');
  }

  async purchaseWithShopify(subscriptionLevel) {
    const subscription = await this.Restangular.all('integrations/shopify/subscription').post({
      billingDetails: this.Authentication.user.org.name,
      subscriptionLevel: subscriptionLevel || SUBSCRIPTION_LEVELS.PREMIUM,
    });

    this._updateOrgBasedOnSubscription(subscription);

    if (subscription.isPremium) {
      this.Authentication.user.hasAccessToLaborCost =
        this.Authentication.user.isAllowedToManageSubscription;
    }

    return subscription;
  }

  postSubscriptionPurchase = async (subscription) => {
    this._updateOrgBasedOnSubscription(subscription);

    if (subscription.isPremium) {
      this.Authentication.user.hasAccessToLaborCost =
        this.Authentication.user.isAllowedToManageSubscription;
    }

    const description = `You have now upgraded to ${this.toSubscriptionNameFilter(
      subscription.subscriptionLevel
    )}, which makes all\n ${this.toSubscriptionNameFilter(
      subscription.subscriptionLevel
    )} features accessible for the period of the subscription.`;

    this.GoogleAnalytics.push({
      event: 'Org_Upgrade',
      category: 'Upgraded',
      label: 'Org_Upgrade_Custom Event',
    });
    this.$state.go('account.overview.billing', {}, { reload: true });

    this.openCongratulationsModal(description, 'Get started');
  };

  async getBillingInfo({ fromCache = false } = {}) {
    if (fromCache && this._billingInfo) {
      // this prevents loading billing info twice on page load
      return this._billingInfo;
    }

    this._billingInfo = await this.Restangular.one('account/org/subscription').get();
    return this._billingInfo;
  }

  async updateBillingDetails(billingDetails) {
    this._billingInfo = null;
    return await this.Restangular.all('account/org/subscription').customPUT({ billingDetails });
  }

  async updateBillingEmails(billingEmails) {
    this._billingInfo = null;
    return await this.Restangular.all('account/org/subscription').customPUT({ billingEmails });
  }

  async pauseSubscription(date) {
    this._billingInfo = null;
    const subscription = await this.Restangular.one('account/org/subscription/pause').customPOST({
      date: this.Utils.formatDate(date),
    });
    this.$state.go('account.overview.billing', {}, { reload: true });
    return subscription;
  }

  async updatePauseSubscription(date) {
    this._billingInfo = null;
    const subscription = await this.Restangular.one('account/org/subscription/pause').customPUT({
      date: this.Utils.formatDate(date),
    });
    this.$state.go('account.overview.billing', {}, { reload: true });
    return subscription;
  }

  async getInvoices() {
    return await this.Invoices.getList();
  }

  openPremiumGuardModal(premiumFeatureName, stateAfter, premiumFeature) {
    if (premiumFeatureName && premiumFeatureName in PREMIUM_FEATURES_INFO) {
      return this._openGuardModal(
        modalGuardUrl,
        stateAfter,
        premiumFeatureName,
        'premium-guard-modal',
        premiumFeature
      );
    }

    return this.openPricingModal(stateAfter);
  }

  openPricingModal(stateAfter) {
    return this._openGuardModal(modalPricingUrl, stateAfter);
  }

  // obsolete method, use react/account/utils/getRequiredSubsctiptionNameForFeature.ts instead
  getRequiredSubsctiptionNameForFeature = (paidFeature) => {
    if (this.Authentication.user.org.isToastBilling) {
      return 'Scheduling Pro';
    }

    return paidFeature && !this.Authentication.isOldPremium
      ? this.SUBSCRIPTION_NAMES[this.Authentication.user.orgSettings.premiumFeatures[paidFeature]]
      : this.SUBSCRIPTION_NAMES[1];
  };

  _openGuardModal(
    html,
    stateAfter,
    premiumFeatureName,
    windowClass = '',
    premiumFeature = premiumFeatureName
  ) {
    const ctrl = this;

    return this.$uibModal
      .open({
        size: '1200',
        windowClass: `modal-dialog-with-padding ${windowClass}`,
        templateUrl: html,
        controllerAs: 'ctrl',
        resolve: {
          startTrial: () => this.startTrial.bind(this),
          trialStartCongratulations: () => this.trialStartCongratulations.bind(this),
        },
        controller(
          $uibModalInstance,
          Authentication,
          startTrial,
          trialStartCongratulations,
          $state
        ) {
          'ngInject';

          this.PREMIUM_FEATURES_INFO = PREMIUM_FEATURES_INFO;
          this.premiumFeatureName = premiumFeatureName;
          this.Authentication = Authentication;
          this.stateAfter = stateAfter;
          this.premiumFeatureInfo = PREMIUM_FEATURES_INFO[premiumFeatureName] || {};
          this.requiredSubscriptionName =
            ctrl.getRequiredSubsctiptionNameForFeature(premiumFeature);
          this.requiredSubscriptionLevel = Object.values(SUBSCRIPTION_LEVELS)
            .map(Number)
            .sort()
            .find(
              (level) => level >= Authentication.user.orgSettings.premiumFeatures[premiumFeature]
            ) || SUBSCRIPTION_LEVELS.PREMIUM;

          this.closeModal = () => {
            ctrl.$rootScope.$broadcast('closeOutstandingModals');
            $uibModalInstance.dismiss();
          };

          this.startTrial = async () => {
            await startTrial(true);
            trialStartCongratulations();
            $uibModalInstance.close();
          };

          this.goToBilling = () => {
            $state.go('account.overview.billing', { showPricing: true });
          };

          this.purchasePlan = () => {
            $state.go('account.overview.purchase', { plan: this.requiredSubscriptionLevel });
          };
        },
      })
      .result.ignoreCancel();
  }

  openCongratulationsModal(description, button) {
    return this.$uibModal.open({
      windowClass: 'sling-tour-modal sling-animate',
      templateUrl: modalCongratulationsUrl,
      controllerAs: 'ctrl',
      controller() {
        this.description = description;
        this.button = button;
      },
    }).result;
  }

  shouldShowTrialActivationReminder(flag) {
    if (!this.Authentication.user) {
      return false;
    }

    const delayInDays = this.Authentication.user.org.isToastBilling ? 0 : 14;

    return (
      !this.Authentication.user.settings.clientFlags[flag] &&
      this.Authentication.user.isManager &&
      !this.Authentication.user.org.premiumTrialStartDate &&
      !this.Authentication.user.org.subscriptionStartDate &&
      !this.Authentication.user.org.subscriptionCancelDate &&
      this.Authentication.user.joinedAt &&
      new Date() - new Date(this.Authentication.user.joinedAt) >= 1000 * 3600 * 24 * delayInDays
    );
  }

  async openTrialOfferModal(flag) {
    let modalShownDate;

    const templateUrl = this.Authentication.user.org.isToastBilling
      ? modalToastTrialOfferUrl
      : modalTrialOfferUrl;
    const windowClasses = ['sling-tour-modal', 'sling-animate'];
    if (this.Authentication.user.org.isToastBilling) {
      windowClasses.push('light-theme');
      windowClasses.push('aspect-ratio-2-1');
    }
    const {
      trialActivationModalShown,
      trialActivationModalStartedSeconds,
      trialActivationModalDismissedSeconds,
    } = this.SlingAnalytics.analyticsEvents;
    try {
      this.SlingAnalytics.track(trialActivationModalShown, null);
      modalShownDate = Date.now();
      await this.$uibModal.open({
        windowClass: windowClasses.join(' '),
        templateUrl,
      }).result;

      this.startTrial();
      const secondsShown = Math.floor((Date.now() - modalShownDate) / 1000);
      this.SlingAnalytics.track(trialActivationModalStartedSeconds, secondsShown);
    } catch {
      const secondsShown = Math.floor((Date.now() - modalShownDate) / 1000);
      this.SlingAnalytics.track(trialActivationModalDismissedSeconds, secondsShown);
    }

    this.Authentication.user.settings.clientFlags[flag] = this.Utils.getNowDate();
    this.Authentication.syncSettings();
  }

  get slingFeatures() {
    /* eslint-disable quote-props, key-spacing, dot-notation */
    const { premiumFeatures } = this.Authentication.user.orgSettings;

    const featureToAccess = (featureName) => premiumFeatures[featureName] || SLING_FREE;

    let features = {
      'Shift scheduling': FEATURE_ACCESS['Shift scheduling'],
      'Shift templates': FEATURE_ACCESS['Shift templates'],
      'Shift alarms': featureToAccess('alarms'),
      'Shift offers': FEATURE_ACCESS['Shift offers'],
      'Available shifts': FEATURE_ACCESS['Available shifts'],
      'Time off requests': featureToAccess('timeOff'),
      Unavailability: featureToAccess('unavailability'),
      'Private and group chats': featureToAccess('messages'),
      'News sharing': featureToAccess('newsfeed'),
      'Long term scheduling': FEATURE_ACCESS['Long term scheduling'],
      'Unlimited employees': FEATURE_ACCESS['Unlimited employees'],
      'Multiple locations': FEATURE_ACCESS['Multiple locations'],
      'Mobile time clock': featureToAccess('clockIn'),
      'Timesheet approvals': featureToAccess('clockIn'),
      'Early clock-in prevention': featureToAccess('clockIn'),
      'Auto clock-out': featureToAccess('clockIn'),
      Geofencing: featureToAccess('clockIn'),
      'Shift swaps': FEATURE_ACCESS['Shift swaps'],
      'Slots on available shifts': FEATURE_ACCESS['Slots on available shifts'],
      'Labor cost management': featureToAccess('laborCost'),
      Budgeting: featureToAccess('laborCost'),
      'Overtime tracking': featureToAccess('laborCost'),
      'Holiday pay': featureToAccess('laborCost'),
      'Google Calendar synchronization': FEATURE_ACCESS['Google Calendar synchronization'],
      'Day parts': FEATURE_ACCESS['Day parts'],
      'SMS shift alarms': FEATURE_ACCESS['SMS shift alarms'],
      'Kiosk time clock': featureToAccess('kiosk'),
      'Auto-assigning': FEATURE_ACCESS['Auto-assigning'],
      Reports: featureToAccess('reports'),
      'No shows': featureToAccess('noShow'),
      'Sick callouts': featureToAccess('sickCallout'),
      'Late arrivals': FEATURE_ACCESS['Late arrivals'],
      'Salary support': FEATURE_ACCESS['Salary support'],
      'Newsfeed page settings': FEATURE_ACCESS['Newsfeed page settings'],
      'Conversation settings': FEATURE_ACCESS['Conversation settings'],
      'PTO tracking': featureToAccess('leaveTypes'),
      Tags: featureToAccess('tags'),
      'Shift tasks': FEATURE_ACCESS['Shift tasks'],
      'Shift acceptance': FEATURE_ACCESS['Shift acceptance'],
      'Dedicated account management': FEATURE_ACCESS['Dedicated account management'],
    };
    /* eslint-enable quote-props, key-spacing, dot-notation */

    if (this.Authentication.isOldPremium) {
      const castForOldPremiums = (level) => (level === SLING_OLD_PREMIUM ? SLING_PREMIUM : level);
      features = Object.entries(features).reduce((agg, [key, value]) => {
        if (value < 3) {
          agg[key] = castForOldPremiums(value);
        }

        return agg;
      }, {});
    }

    return features;
  }

  async openTrialReactivationModal(first = false) {
    let analyticsEvent;
    if (first) {
      analyticsEvent = this.SlingAnalytics.analyticsEvents.premiumTrialReactivatedFirst;
    } else {
      analyticsEvent = this.SlingAnalytics.analyticsEvents.premiumTrialReactivatedCyclical;
    }

    try {
      await this.$uibModal.open({
        windowClass: 'sling-animate',
        templateUrl: first ? modalTrialReactivationFirstUrl : modalTrialReactivationCycleUrl,
        backdrop: 'static',
        keyboard: false,
        size: '845',
      }).result;

      this.startTrial();
      this.SlingAnalytics.track(analyticsEvent, 'Yes');
    } catch {
      this.SlingAnalytics.track(analyticsEvent, 'No');
      this.Authentication.user.settings.clientFlags.trialReactivationDismissed =
        this.Utils.getNowDate();
      this.Authentication.syncSettings();
    }
  }

  async showAccountRelatedModalsOnRefresh() {
    const flag = this.Authentication.user.org.isToastBilling
      ? 'toastTrialReminderModalShown'
      : 'trialOffer2ModalShown';
    if (this.shouldShowTrialActivationReminder(flag)) {
      this.openTrialOfferModal(flag);
      return;
    }

    const popupShown = this.tryShowPremiumReactivationPopup();
    if (popupShown || !this.Authentication.user.isSubscriber) {
      return;
    }

    if (this._shouldOpenPaymentFailedModal()) {
      this.openPaymentFailedModal();
      return;
    }

    if (this._shouldOpenCreditCardExpiresSoonModal()) {
      this.openCardExpiresSoonModal();
      return;
    }

    if (this.maybeOpenAutoAssigningModal()) {
      return;
    }
  }

  maybeOpenAutoAssigningModal() {
    const { user } = this.Authentication;

    if (user.settings.clientFlags.autoAssigningAnnouncementModalIrrelevant) {
      return false;
    }

    if (!user.isManager) {
      return false;
    }

    let mode = AUTO_ASSIGNING_MODAL_MODES.HAS_ACCESS;

    if (user.org.subscriptionLevel < user.orgSettings.premiumFeatures.autoAssigning) {
      if (user.orgSettings.isAutoAssigningTrialActive) {
        mode = AUTO_ASSIGNING_MODAL_MODES.LAUNCH_TRIAL;
      } else {
        mode = AUTO_ASSIGNING_MODAL_MODES.AFTER_TRIAL;
      }
    }

    if (user.settings.clientFlags[`autoAssigningAnnouncementModal-${mode}-dismissed`]) {
      return false;
    }

    this.openAutoAssigningModal(mode);
    return true;
  }

  async openAutoAssigningModal(mode) {
    try {
      await this.$uibModal.open({
        size: '700',
        templateUrl: modalAutoAssigninglUrl,
        controllerAs: 'ctrl',
        controller() {
          this.mode = mode;
          this.AUTO_ASSIGNING_MODAL_MODES = AUTO_ASSIGNING_MODAL_MODES;
        },
      }).result;

      if (mode === AUTO_ASSIGNING_MODAL_MODES.AFTER_TRIAL) {
        this.$state.go('account.overview.billing');
      }
    } finally {
      this.Authentication.saveClientFlags({
        [`autoAssigningAnnouncementModal-${mode}-dismissed`]: this.Utils.formatDate(
          this.Utils.getNowDate()
        ),
      });
    }
  }

  _shouldOpenPaymentFailedModal() {
    const { user } = this.Authentication;
    const paymentFailedDate = user.org.subscriptionPaymentFailedDate;
    return (
      paymentFailedDate &&
      this.Utils.subtractDates(this.Utils.getNowDate(), paymentFailedDate, 'days') < 30 &&
      user.settings.clientFlags.subscriptionPaymentFailedDateDismissed !==
        this.Utils.formatDate(paymentFailedDate)
    );
  }

  _shouldOpenCreditCardExpiresSoonModal() {
    const { user } = this.Authentication;
    const cardExpiryDate = user.org.subscriptionCreditCardExpiryDate;
    return (
      cardExpiryDate &&
      this.Utils.subtractDates(cardExpiryDate, this.Utils.getNowDate(), 'days') < 28 &&
      user.settings.clientFlags.subscriptionCreditCardExpiryDateDismissed !==
        this.Utils.formatDate(cardExpiryDate)
    );
  }

  async openPaymentFailedModal() {
    const { subscriptionPaymentFailed } = this.SlingAnalytics.analyticsEvents;

    try {
      await this.$uibModal.open({
        size: '500',
        windowClass: 'modal-dialog-with-padding',
        templateUrl: modalPaymentFailedUrl,
      }).result;
      this.SlingAnalytics.track(subscriptionPaymentFailed, 'clicked');
      this.$state.go('account.overview.billing');
    } catch {
      this.SlingAnalytics.track(subscriptionPaymentFailed, 'dismissed');
    } finally {
      this.Authentication.saveClientFlags({
        subscriptionPaymentFailedDateDismissed: this.Utils.formatDate(
          this.Authentication.user.org.subscriptionPaymentFailedDate
        ),
      });
    }
  }

  async openCardExpiresSoonModal() {
    const { subscriptionCreditCardExpiresSoon } = this.SlingAnalytics.analyticsEvents;
    const billing = await this.getBillingInfo();
    try {
      await this.$uibModal.open({
        size: '500',
        windowClass: 'modal-dialog-with-padding',
        templateUrl: modalCardExpiresSoonUrl,
        resolve: {
          billingInfo: () => billing,
        },
        controllerAs: 'ctrl',
        controller(billingInfo) {
          'ngInject';

          this.billingInfo = billingInfo;
        },
      }).result;
      this.SlingAnalytics.track(subscriptionCreditCardExpiresSoon, 'clicked');
      this.$state.go('account.overview.billing');
    } catch {
      this.SlingAnalytics.track(subscriptionCreditCardExpiresSoon, 'dismissed');
    } finally {
      this.Authentication.saveClientFlags({
        subscriptionCreditCardExpiryDateDismissed: this.Utils.formatDate(
          this.Authentication.user.org.subscriptionCreditCardExpiryDate
        ),
      });
    }
  }

  async initializeACHPayments(subscriptionLevel) {
    return (
      await this.Authentication.sendRequest({
        method: 'post',
        path: 'account/org/subscription/ach-credit-transfer',
        payload: {
          subscriptionLevel,
        },
      })
    ).data;
  }

  async cancelACH() {
    return (
      await this.Authentication.sendRequest({
        method: 'delete',
        path: 'account/org/subscription/ach-credit-transfer',
      })
    ).data;
  }

  async grant50PercentOff() {
    const subscription = await this.Restangular.all(
      'account/org/subscription/50_percent_off_year'
    ).post();
    this.Restangular.restangularizeElement(null, subscription, 'account/org/subscription');
    this._updateOrgBasedOnSubscription(subscription);
    return subscription;
  }

  async getUpgradeCost() {
    return (
      await this.Authentication.sendRequest({
        method: 'get',
        path: 'account/org/subscription/upgradecost',
      })
    ).data;
  }

  // obsolete method, use react/account/utils/isFeatureBannerVisible.ts instead
  isFeatureBannerVisible = (feature, showAfterPurchase) =>
    (!this.Authentication.user.org.subscriptionStartDate || showAfterPurchase) &&
    this.Authentication.isOrgFeatureEnabled(feature) &&
    !this.Authentication.user.settings.clientFlags[`premiumFeatureBanner-${feature}-dismissed`];
}

export default AccountService;
